import { Component, inject, input, OnInit, output, signal, TemplateRef, viewChild } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import {
  FormActionButtonsComponent,
  FormDropdownTreeComponent,
  FormInputComponent,
} from "../../../../shared/components";
import { PageMode } from "../../../../shared/constants/enums";
import { EntityModelType, StructuredOrgUnit, ValidatorModelType, ValueText } from "../../../../shared/models";
import { LanguageService, ValidatorApiService } from "../../../../shared/services";

@Component({
  selector: "adaa-organization-unit-editor",
  imports: [
    TranslatePipe,
    FontAwesomeModule,
    ReactiveFormsModule,
    FormInputComponent,
    FormDropdownTreeComponent,
    FormActionButtonsComponent,
  ],
  templateUrl: "./organization-unit-editor.html",
  styleUrl: "../organization-unit.scss",
})
export class OrganizationUnitEditorComponent implements OnInit {
  private readonly _modalService = inject(NgbModal);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _toastrService = inject(ToastrService);
  private readonly _languageService = inject(LanguageService);
  private readonly _validatorApiService = inject(ValidatorApiService);

  pageMode = input.required<string>();
  parentOrgUnit = input.required<StructuredOrgUnit | undefined>();
  entity = input.required<EntityModelType>();
  orgUnitOptions = input.required<ValueText[]>();
  formValidation = input.required<ValidatorModelType | undefined>();
  submitForm = output<{ nameAE: string; nameEN: string; parentOrgUnit: number }>();

  submitted = signal<boolean>(false);
  PageMode = PageMode;
  orgUnitForm: FormGroup = new FormGroup({});

  modal = viewChild.required<TemplateRef<unknown>>("content");

  public ngOnInit(): void {
    this._prepareForm();
  }

  private _prepareForm() {
    this.orgUnitForm = this._formBuilder.group({
      nameAE: null,
      nameEN: null,
      parentOrgUnit: null,
    });

    const validation = this.formValidation();
    if (!validation) return;
    this.orgUnitForm = this._validatorApiService.setValidationFromData(validation, this.orgUnitForm);
  }

  public open() {
    const modalRef: NgbModalRef = this._modalService.open(this.modal(), {
      animation: true,
      scrollable: false,
      keyboard: false,
      size: "lg",
      centered: true,
      modalDialogClass: this._languageService.modalDirection(),
    });

    modalRef.dismissed.subscribe(() => this._resetForm());
    modalRef.closed.subscribe(() => this._resetForm());
  }

  public checkInvalid(control: string) {
    return (
      this.orgUnitForm.controls[control].errors?.["required"] &&
      (this.orgUnitForm.get(control)?.dirty || this.submitted())
    );
  }

  public submit() {
    this.submitted.set(true);
    if (this.orgUnitForm.invalid) {
      this._toastrService.error(this._languageService.translate("common.form.label.missing_information"));
      return;
    }
    this.submitForm.emit(this.orgUnitForm.value);
  }

  private _resetForm() {
    this.orgUnitForm.reset();
    this.orgUnitForm.markAsPristine();
    this.orgUnitForm.markAsUntouched();
    this.submitted.set(false);
  }
}
